'use client';

/**
 * FlattenAllButton — the GLOBAL engine-only panic exit, in the topbar.
 *
 * Opening it auto-runs a system-wide DRY-RUN so the operator sees every
 * subscription that would be closed (engine-only, manual positions untouched)
 * before committing. Executing needs a reason + typed FLATTEN. Reduce-only
 * closes pass even a closed kill-switch — this is the "get the whole desk flat"
 * button. Distinct from the per-subscription 🔥 on the broker-account page.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Flame, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { flattenAll } from '@/lib/api/admin-flatten';
import { cn } from '@/lib/util/cn';

const sgn = (n: number) => (n > 0 ? `+${n}` : `${n}`);

export function FlattenAllButton() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Flatten ALL — close every engine position across all accounts"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1',
          'border-crit/60 bg-crit-soft text-2xs font-semibold',
          'text-crit transition-colors hover:brightness-110',
        )}
        aria-label="Flatten all engine positions"
      >
        <Flame className="h-3 w-3 stroke-[1.6]" aria-hidden />
        Flatten all
      </button>
      {open ? <FlattenAllDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function FlattenAllDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  const dry = useMutation({
    mutationFn: () => flattenAll({ execute: false, reason: 'preview' }),
  });
  React.useEffect(() => {
    dry.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = useMutation({
    mutationFn: () => flattenAll({ execute: true, reason: reason.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  const result = exec.data?.data ?? null;
  const plan = result ?? dry.data?.data ?? null;
  const err = exec.error ?? dry.error ?? null;
  const withPlans = plan?.results.filter((r) => r.plans.length > 0) ?? [];
  const totalLegs = withPlans.reduce((n, r) => n + r.plans.length, 0);
  const blocked = plan?.results.some((r) => r.errors.length > 0) ?? false;

  const canExecute =
    result == null &&
    totalLegs > 0 &&
    reason.trim().length >= 4 &&
    confirm.trim().toUpperCase() === 'FLATTEN' &&
    !exec.isPending;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) {onClose();} }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-4 w-4 text-crit" />
            Flatten ALL engine positions
          </DialogTitle>
          <DialogDescription>
            Closes the positions the engine opened across EVERY subscription /
            account (market orders). Manual positions are left untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {dry.isPending ? (
            <p className="flex items-center gap-1.5 text-2xs text-fg-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> scanning all subscriptions…
            </p>
          ) : null}

          {plan ? (
            totalLegs === 0 ? (
              <div className="rounded border border-border bg-surface-inset px-3 py-2 text-2xs text-fg-muted">
                No engine positions open anywhere — nothing to close.
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-2xs font-medium text-fg-subtle">
                  {result ? 'Closed' : 'Will close'} — {totalLegs} leg
                  {totalLegs === 1 ? '' : 's'} across {withPlans.length} subscription
                  {withPlans.length === 1 ? '' : 's'}
                </div>
                {withPlans.map((r) =>
                  r.plans.map((p) => (
                    <div key={`${r.subscription_id}-${p.symbol}`} className="flex flex-wrap items-center gap-2 font-mono text-2xs">
                      <Badge tone="warn" size="sm">
                        {p.side} {p.qty} {p.symbol}
                      </Badge>
                      <span className="text-fg-muted">
                        sub {r.subscription_id.slice(0, 8)} · engine {sgn(p.engine_net)} ·
                        manual residual {sgn(p.manual_residual)} left untouched
                      </span>
                    </div>
                  )),
                )}
                {plan.results.flatMap((r) => r.alarms).map((a, i) => (
                  <div key={`al${i}`} className="flex items-start gap-1.5 rounded border border-crit/40 bg-crit-soft px-2 py-1 text-2xs text-crit">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {a}
                  </div>
                ))}
                {plan.results.flatMap((r) => r.errors).map((e, i) => (
                  <div key={`er${i}`} className="flex items-start gap-1.5 rounded border border-warn/40 bg-warn-soft px-2 py-1 text-2xs text-warn">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {e}
                  </div>
                ))}
              </div>
            )
          ) : null}

          {result ? (
            <div
              className={cn(
                'rounded border px-3 py-2 text-2xs',
                result.all_flat && result.failed === 0
                  ? 'border-long/50 bg-long-soft text-long'
                  : 'border-crit/50 bg-crit-soft text-crit',
              )}
            >
              {result.subscription_count} subscription(s) · placed {result.placed} ·
              failed {result.failed} ·{' '}
              {result.all_flat ? 'ALL engine positions FLAT ✅' : 'NOT fully flat — review ⚠️'}
            </div>
          ) : null}

          {result == null && totalLegs > 0 && !blocked ? (
            <div className="space-y-2 border-t border-divider pt-3">
              <label className="block">
                <span className="text-2xs font-medium text-fg-subtle">
                  Reason (audited)
                </span>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you flattening everything?"
                  rows={2}
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="text-2xs font-medium text-fg-subtle">
                  Type <span className="font-mono text-crit">FLATTEN</span> to confirm
                </span>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="FLATTEN"
                  autoComplete="off"
                />
              </label>
            </div>
          ) : null}

          {err ? (
            <div className="flex items-start gap-2 rounded border border-crit/50 bg-crit-soft px-3 py-2 text-2xs text-crit">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{err instanceof Error ? err.message : 'Request failed'}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={exec.isPending}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {result == null ? (
            <Button variant="danger" size="sm" disabled={!canExecute} onClick={() => exec.mutate()}>
              {exec.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Flame className="h-3.5 w-3.5" />}
              Flatten everything
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
